import type { ReactNode } from 'react';
import { styled } from '@classify/react-core-components/styles';

import Typography from '@classify/react-core-components/Typography';
import Card from '@classify/react-core-components/Card';
import { alpha } from '@mui/system';
import Box from '@classify/react-core-components/Box';

export interface Props {
  hasForcedRowLayout?: boolean;
  thumbnail: string;
  fieldColor: string;
  field: string;
  title: string;
  description: string;
  children?: ReactNode;
  link?: string | null;
}
const Root = styled('a')(({ theme: { shadows } }) => ({
  display: 'block',
  textDecoration: 'none',
  '&[href] > :hover': {
    boxShadow: shadows.containedInteractionRaised,
  },
}));

const PathCard = styled(Card, {
  shouldForwardProp: (prop) => !['hasForcedRowLayout'].includes(prop as string),
})<{ hasForcedRowLayout: boolean }>(({ theme: { breakpoints }, hasForcedRowLayout }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  [breakpoints.up('sm')]: {
    flexDirection: hasForcedRowLayout ? 'row' : 'column',
    maxWidth: hasForcedRowLayout && 'unset',
  },
  [breakpoints.up('md')]: {
    maxWidth: hasForcedRowLayout ? 'unset' : 704,
    flexDirection: 'row',
  },
  [breakpoints.up('lg')]: {
    maxWidth: 'unset',
  },
}));
const PathInfoContainer = styled('div')(({ theme: { spacing } }) => ({
  padding: spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignContent: 'flex-start',
}));

const ChildrenWrapper = styled('div')(({ theme: { spacing } }) => ({
  width: '100%',
  flexGrow: 1,
  display: 'flex',
  alignItems: 'flex-end',
  marginTop: spacing(1),
}));

const PathIllustration = styled(Box, {
  shouldForwardProp: (prop) => !['hasForcedRowLayout'].includes(prop as string),
})<{ hasForcedRowLayout: boolean }>(({ theme: { breakpoints }, hasForcedRowLayout }) => ({
  position: 'relative',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'bottom',
  minHeight: 104,
  [breakpoints.up('sm')]: {
    minWidth: 176,
    minHeight: hasForcedRowLayout ? 'unset' : 168,
  },
  [breakpoints.up('md')]: {
    width: 288,
    minHeight: 168,
  },
}));

const PathDiscoveryItem = ({
  hasForcedRowLayout = false,
  thumbnail,
  fieldColor,
  field,
  title,
  description,
  children = null,
  link = null,
  ...otherProps
}: Props) => (
  <Root href={link} data-testid="path-discovery-item" {...otherProps}>
    <PathCard hasForcedRowLayout={hasForcedRowLayout} data-testid="path-discovery-card">
      <PathIllustration
        hasForcedRowLayout={hasForcedRowLayout}
        sx={{
          backgroundImage: `url(${thumbnail})`,
          backgroundColor: alpha(fieldColor, 0.32),
        }}
      />
      <PathInfoContainer>
        <Typography variant="overline" sx={{ color: fieldColor }}>
          {field}
        </Typography>
        <Typography variant="h5" component="span" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="bodySmall" sx={{ display: 'inline-block', maxWidth: 768 }}>
          {description}
        </Typography>
        {children && <ChildrenWrapper>{children}</ChildrenWrapper>}
      </PathInfoContainer>
    </PathCard>
  </Root>
);

export default PathDiscoveryItem;
